import { describeModbusStatus } from "@/features/devices/modbusDiagnostic";
import type { LatestPort } from "@/features/devices/types";
import { summarizeModbusHealth } from "../../modbusHealth";
import type { DashboardWidget, DataQuality, ResolvedWidgetValue } from "../../types";
import { formatWidgetRelative } from "./format";
import { HeroCard, HeroMeaning, type HeroTone } from "./HeroCard";
import { effectiveQuality } from "./quality";

interface ModbusHealthWidgetProps {
  widget: DashboardWidget;
  /** Frescor da fonte ancora; os estados MB do slot chegam no mesmo envelope. */
  value: ResolvedWidgetValue;
  /** Portas MB do MESMO slot da âncora. Vazio = device sem Modbus. */
  modbusPorts: LatestPort[];
  editable?: boolean;
  onConfigure?: () => void;
}

/**
 * Saúde do barramento Modbus: um card técnico compacto no lugar de um
 * diagnóstico por registrador.
 *
 * Com 16 pontos, materializar 16 cards de diagnóstico afogaria o painel — e o
 * operador ainda teria que somar de cabeça quantos estão mudos. A pergunta que
 * ele faz é "o barramento está bem?", e essa pergunta não pertence a nenhum
 * ponto individual.
 *
 * Os pontos em falha são LISTADOS por porta, não só contados: "3 em timeout"
 * manda conferir o barramento inteiro; "timeout nas portas 7, 8 e 9" manda
 * olhar um trecho específico da fiação.
 */
/**
 * Portas de uma falha, em texto. `count` conta todos os pontos no estado;
 * `ports` so os de indice conhecido — a diferenca aparece em vez de sumir.
 */
function portsTitle(failure: { count: number; ports: number[] }): string {
  const listed = failure.ports.map((port) => `p${port}`).join(", ");
  const unknown = failure.count - failure.ports.length;
  if (unknown <= 0) return listed;
  const rest = unknown === 1 ? "1 sem porta" : `${unknown} sem porta`;
  return listed ? `${listed} · ${rest}` : rest;
}

export function ModbusHealthWidget({
  widget,
  value,
  modbusPorts,
  editable,
  onConfigure,
}: ModbusHealthWidgetProps) {
  const health = summarizeModbusHealth(modbusPorts);
  const hasFailures = health.failures.length > 0;
  const quality = effectiveQuality(value);
  const current = quality === "live";

  // `never` em TODOS é o estado de boot, não uma falha: a placa subiu e ainda
  // não fechou a primeira transação. Tratar como perigo faria o card gritar
  // vermelho em todo restart.
  const cardTone: "ok" | "warn" | "danger" = !current
    ? quality === "stale"
      ? "warn"
      : "ok"
    : health.allNever
      ? "warn"
      : hasFailures
        ? health.failures.some((failure) => failure.status !== "never")
          ? "danger"
          : "warn"
        : "ok";
  const staleMeaning =
    quality === "live" ? null : modbusAbsenceMeaning(quality);
  const countFootnote = health.total > 0 ? `${health.total} pontos declarados` : null;
  const lastReading = value.measuredAt
    ? `Última leitura ${formatWidgetRelative(value.measuredAt) ?? "recebida"}`
    : null;
  const footnote = current
    ? countFootnote
    : [lastReading, countFootnote].filter(Boolean).join(" · ") || null;

  return (
    <HeroCard
      title={widget.title}
      caption="Barramento Modbus"
      tone={cardTone}
      alarm={widget.settings.alarmEnabled}
      editable={editable}
      onConfigure={onConfigure}
      footnote={footnote}
    >
      {!current ? (
        <>
          <div className="w-hero__value">
            --
            {health.total > 0 ? (
              <span className="w-hero__unit">de {health.total} pontos</span>
            ) : null}
          </div>
          <HeroMeaning tone={staleMeaning!.tone} animated={staleMeaning!.animated}>
            {staleMeaning!.text}
          </HeroMeaning>
        </>
      ) : health.total === 0 ? (
        <p className="w-hero__trend-empty">Nenhum ponto Modbus neste equipamento.</p>
      ) : (
        <>
          <div className="w-hero__value">
            {health.ok}
            <span className="w-hero__unit">de {health.total} ok</span>
          </div>

          {health.allNever ? (
            <HeroMeaning tone="warn">Aguardando a primeira leitura do barramento</HeroMeaning>
          ) : !hasFailures ? (
            <HeroMeaning tone="ok">Todos os pontos respondendo</HeroMeaning>
          ) : null}

          {hasFailures && !health.allNever ? (
            <ul className="w-mbhealth">
              {health.failures.map((failure) => {
                const presentation = describeModbusStatus(failure.status);
                return (
                  <li key={failure.status} className="w-mbhealth__row">
                    <span
                      className={`w-mbhealth__tag w-mbhealth__tag--${presentation.tone}`}
                    >
                      {presentation.label}
                    </span>
                    <span className="w-mbhealth__count">{failure.count}</span>
                    {/* A porta é o que leva o operador ao ponto certo da
                        fiação; sem ela o card só diz que "algo" está mudo.
                        A lista trunca em card estreito, então o `title` mantém
                        as portas cortadas alcançáveis.
                        Quando algum ponto chega sem índice legível a lista fica
                        mais curta que a contagem — dizer isso é melhor que
                        deixar o operador conferir na mão por que "3 em timeout"
                        aponta só duas portas. */}
                    <span className="w-mbhealth__ports" title={portsTitle(failure)}>
                      {portsTitle(failure)}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </>
      )}
    </HeroCard>
  );
}

/**
 * Frescor MQTT e diagnostico RTU sao eixos independentes. Um timeout em pacote
 * recente e falha atual; um `ok` de ontem nao pode continuar dizendo que o
 * barramento responde. Quando o envelope nao e atual, nenhuma contagem antiga
 * e apresentada como estado presente.
 */
function modbusAbsenceMeaning(
  quality: Exclude<DataQuality, "live">,
): { tone: HeroTone; text: string; animated?: boolean } {
  switch (quality) {
    case "waiting":
      return { tone: "muted", text: "Aguardando telemetria do barramento", animated: true };
    case "stale":
      return { tone: "warn", text: "Sem leitura recente do barramento" };
    case "offline":
      return { tone: "muted", text: "Gateway offline · estado do barramento desconhecido" };
  }
}
